<template>
  <header id="header" class="header">
    <div class="header__content">
      <div :class="`header__logo--${env('WHITE_LABEL_KEY')}`" class="header__logo"></div>
      <div id="hotel-name" :class="['header__name', {'privacy--blur': displayHotelName}]" @click="onClickHeader($event)">
        <select v-if="showDropdown && hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" :value="$root.hotel" @change="switchHotel($event)">
          <option v-for="hotel in hotels" :key="hotel.hotelId" :value="hotel.hotelId">{{ hotel.subdomain }} ({{ hotel.hotelId }}, hotel::{{ hotel.hotelNumber }}, {{ hotel.countryCode }}, {{ hotel.roomCount }} rooms) - {{ hotel.whiteLabel }}</option>
        </select>
        <portal-target ref="portal"
                       name="headerTitle"
                       slim
                       @change="changeTitle">
          {{ hotelName }}
        </portal-target>
        <div :class="[{'header__save-in-progress--active': saveInProgress}]" class="header__save-in-progress">
          <span>{{ $t('layout.appHeader.saving') }}</span>
          <svg width="11px"
               height="14px"
               viewBox="0 0 11 14"
               version="1.1"
               xmlns="http://www.w3.org/2000/svg"
               xmlns:xlink="http://www.w3.org/1999/xlink">
            <g stroke="none"
               stroke-width="1"
               fill="none"
               fill-rule="evenodd"
               stroke-linecap="round">
              <g transform="translate(-11.000000, -9.000000)" stroke="#FFFFFF">
                <g class="loading-burger" transform="translate(11.000000, 9.000000)">
                  <path d="M0.5,1 L10.5,1" class="loading-burger__line loading-burger__line--1"></path>
                  <path d="M0.5,5 L10.5,5" class="loading-burger__line loading-burger__line--2"></path>
                  <path d="M0.5,9 L10.5,9" class="loading-burger__line loading-burger__line--3"></path>
                  <path d="M0.5,13 L10.5,13" class="loading-burger__line loading-burger__line--4"></path>
                </g>
              </g>
            </g>
          </svg>
        </div>
      </div>
      <div class="header__right">
        <div id="username" class="header__right__username hidden visible@desktop visible@hd" @click="$serviceBus.$emit('username.click')">{{ $root.username }}</div>
        <hm-notifications v-if="hasRole('ROLE_USER')" id="header-notifications"/>
        <div :class="{'header__hamburger--active': navVisible}" class="header__hamburger" @click="openMenu">
          <svg viewBox="0 0 28 18">
            <g stroke="none"
               stroke-width="1"
               fill="none"
               fill-rule="evenodd"
               stroke-linecap="square">
              <g transform="translate(-333 -21)" stroke="#FFF">
                <g class="svg-burger" transform="translate(334 21)">
                  <path pid="0" d="M.394 9h25.212" class="svg-burger__bun svg-burger__bun--1"></path>
                  <path pid="1" d="M.394 9h25.212" class="svg-burger__meat"></path>
                  <path pid="2" d="M.394 9h25.212" class="svg-burger__bun svg-burger__bun--2"></path>
                </g>
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  </header>
</template>

<script>

import { isNil, isEmpty, isBlank, cloneDeep, orderBy, find } from 'lodash';
import UpdateHotelSettingsMixin from '../../../mixins/UpdateHotelSettingsMixin';
import HotelSettingsMixin from '../../../mixins/HotelSettingsMixin';
import { getToken, setToken, clearToken } from '../../../lib/util/token';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'AppHeader',
  components: { },

  mixins: [HotelmeisterMixin, HotelSettingsMixin, UpdateHotelSettingsMixin, TimerMixin, HotelmeisterMixin],
  props: [],

  head: {
    title: function () {
      return {
        inner: isEmpty(this.titleContent) ? this.hotelName : `${this.hotelName} / ${this.titleContent}`,
      };
    },
  },

  data() {
    return {
      displayHotelName: true,
      titleContent: null,
      showDropdown: false,
    };
  },
  computed: {
    hotelName: function () {
      if (isNil(this.hotelSettings) || isNil(this.hotelSettings.hotel)) return '';

      return this.hotelSettings.hotel.hotelName;
    },
    navVisible: function () {
      return this.$store.getters['layout/navVisible'];
    },
    saveInProgress: function () {
      return this.$store.getters['save/saveInProgress'];
    },
    hotels: function () {
      return orderBy(this.$store.getters['hotel/hotels'], it => it.subdomain);
    },
    hotelSelectOptions: function () {
      return (this.hotels || []).map(hotel => ({
        label: `${hotel.subdomain} (${hotel.hotelId}, hotel::${hotel.hotelNumber}, ${hotel.countryCode}, ${hotel.roomCount} rooms) - ${hotel.whiteLabel}`,
        value: hotel.hotelId,
      }));
    },
  },

  created() {
    if (this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && isEmpty(this.hotels)) {
      this.$store.dispatch('hotel/updateAccessibleHotels');
    }
  },

  methods: {
    env: function (name) {
      return process.env[name];
    },
    openMenu(event) {
      this.$store.dispatch('layout/navVisible', !this.$store.getters['layout/navVisible']);
    },
    changeTitle(newContent, oldContent) {
      this.displayHotelName = isEmpty(this.$refs.portal.passengers);
      this.titleContent = isEmpty(this.$refs.portal.passengers) ? null : this.$refs.portal.passengers[0].text;
      this.$emit('updateHead');
    },
    switchHotel(evt) {
      const target = evt.srcElment || evt.target;
      const newHotelId = target.value;

      this.showDropdown = false;
      this.openHotel(newHotelId);
    },
    openHotel(hotelId) {
      if (hotelId === this.$root.hotel) return;

      this.addUIBreadcrumb('User initiated hotel switch');

      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        if (localStorage.key(i).includes('__hotelSettings')) {
          toRemove.push(localStorage.key(i));
        }
      }
      toRemove.forEach(it => localStorage.removeItem(it));

      // eslint-disable-next-line no-undef
      setToken(hotelId, getToken(this.$root.hotel));
      // eslint-disable-next-line no-undef
      clearToken(this.$root.hotel);
      // this.$root.hotel = hotel.hotelId;
      this.$store.dispatch('hotel/setHotelId', hotelId);
      // this.$serviceBus.$emit('hotelSettings.update.immediate');
      // this.$root.logout();

      // eslint-disable-next-line no-undef
      const route = {
        name: this.$route.name,
        params: cloneDeep(this.$route.params),
        path: this.$route.path,
        query: cloneDeep(this.$route.query),
        meta: cloneDeep(this.$route.meta),
      };
      route.params.hotelId = hotelId;
      route.query.token = getToken(this.$root.hotel);

      const $vm = this;
      const iv = this.setInterval(() => {
        if ($vm.hotelSettings.hotel.id === `hotel::${find(this.hotels, hotel => hotel.hotelId === hotelId).hotelNumber}`) {
          this.clearInterval(iv);
          let routeURL = $vm.$router.resolve(route);

          if (routeURL.href.includes('hotel::') || routeURL.href.includes('tenant::')) {
            route.name = 'home';
            routeURL = $vm.$router.resolve(route);
          }

          window.location.href = routeURL.href;
          this.showDropdown = false;
        }
      }, 250);
    },
    onClickHeader($event) {
      if (isEmpty($event.target) && !isEmpty($event.target.closest('select'))) {
        $event.stopPropagation();
        return;
      }
      this.showDropdown = (!this.showDropdown && this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'));
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
   @import '~styles/components/lib/AppHeader';

  #username {
    user-select: none;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/layout/AppHeader.vue